import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from 'recharts';
import apiClient, { getUploadUrl } from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import AdminLayout from '@/components/AdminLayout';
import MemberAvatar from '@/components/MemberAvatar';
import {
  Eye, CheckCircle, XCircle, Loader2, Clock, User, FileText, CreditCard, AlertCircle,
  TrendingUp, Search, ChevronLeft, ChevronRight, Download, Scale, LayoutDashboard, ListChecks, DollarSign,
  RotateCcw, AlertTriangle
} from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-300',
  verified: 'bg-green-50 text-green-700 border-green-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
};

const RECON_TYPE_LABELS = {
  reverse_verification: 'Reverse Verification',
  correct_verification: 'Correct Verification',
  duplicate_payment: 'Duplicate Payment',
  wrong_member: 'Wrong Member',
  wrong_invoice: 'Wrong Invoice',
  other: 'Other',
};

const AdminPayments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const validTabs = ['dashboard', 'pending', 'all', 'reconciliation', 'reports'];
  const initialTab = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'dashboard';
  const [tab, setTab] = useState(initialTab);
  const [currency, setCurrency] = useState('USD');

  // Pending
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingPage, setPendingPage] = useState(1);
  const PENDING_PAGE_SIZE = 10;

  // Dashboard
  const [overview, setOverview] = useState(null);
  const [topOutstanding, setTopOutstanding] = useState([]);
  const [dashLoading, setDashLoading] = useState(true);

  // All payments
  const [allPayments, setAllPayments] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allSearch, setAllSearch] = useState('');
  const [allStatus, setAllStatus] = useState('all');
  const [allPage, setAllPage] = useState(1);
  const [allTotalPages, setAllTotalPages] = useState(1);
  const [allTotal, setAllTotal] = useState(0);

  // Reconciliation
  const [recons, setRecons] = useState([]);
  const [noticeMap, setNoticeMap] = useState({});
  const [reconLoading, setReconLoading] = useState(false);

  // Reports
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingAllPaid, setExportingAllPaid] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportStatus, setExportStatus] = useState('all');

  // Reconcile dialog
  const [reconcilingPayment, setReconcilingPayment] = useState(null);
  const [reconForm, setReconForm] = useState({ reconciliation_type: 'duplicate_payment', reason: '', notes: '', adjustment_amount: '' });
  const [submittingRecon, setSubmittingRecon] = useState(false);

  // POP viewer
  const [viewingPOP, setViewingPOP] = useState(null);
  const [popImageUrl, setPopImageUrl] = useState(null);
  const [popLoading, setPopLoading] = useState(false);

  // Verification dialog
  const [verifyingPayment, setVerifyingPayment] = useState(null);
  const [verifyAction, setVerifyAction] = useState(null);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentsRes, settingsRes] = await Promise.all([
        apiClient.get('/payments/pending-verification'),
        apiClient.get('/settings/'),
      ]);
      setPayments(paymentsRes.data);
      setPendingPage(1);
      setCurrency(settingsRes.data?.membership_fee?.currency || 'USD');

      const memberIds = [...new Set(paymentsRes.data.map((p) => p.member_id))];
      const memberResults = await Promise.all(
        memberIds.map((id) => apiClient.get(`/admin/members/${id}`).catch(() => null))
      );
      const memberMap = {};
      memberResults.forEach((res, index) => {
        if (res?.data) memberMap[memberIds[index]] = res.data;
      });
      setMembers(memberMap);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const [ovRes, topRes] = await Promise.all([
        apiClient.get('/reports/payments/overview'),
        apiClient.get('/payments/top-outstanding?limit=5'),
      ]);
      setOverview(ovRes.data);
      setTopOutstanding(topRes.data?.members || []);
      if (topRes.data?.currency) setCurrency(topRes.data.currency);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setDashLoading(false);
    }
  }, []);

  const loadAllPayments = useCallback(async () => {
    setAllLoading(true);
    try {
      const params = new URLSearchParams({ page: String(allPage), page_size: '20', status: allStatus });
      if (allSearch.trim()) params.append('search', allSearch.trim());
      const res = await apiClient.get(`/payments/all?${params.toString()}`);
      setAllPayments(res.data.payments || []);
      setAllTotalPages(res.data.total_pages || 1);
      setAllTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setAllLoading(false);
    }
  }, [allPage, allStatus, allSearch]);

  const loadRecons = useCallback(async () => {
    setReconLoading(true);
    try {
      const [recRes, noticeRes] = await Promise.all([
        apiClient.get('/finance/reconciliations?limit=200'),
        apiClient.get('/finance/reconciliation-notices?limit=500').catch(() => ({ data: [] })),
      ]);
      setRecons(recRes.data || []);
      const map = {};
      (noticeRes.data || []).forEach((n) => { map[n.reconciliation_id] = n; });
      setNoticeMap(map);
    } catch (error) {
      toast.error('Failed to load reconciliations');
    } finally {
      setReconLoading(false);
    }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      const res = await apiClient.get('/reports/payments/summary');
      setSummary(res.data);
      if (res.data?.currency) setCurrency(res.data.currency);
    } catch (error) {
      toast.error('Failed to load report summary');
    }
  }, []);

  useEffect(() => { loadPending(); loadDashboard(); }, [loadPending, loadDashboard]);
  useEffect(() => { if (tab === 'all') loadAllPayments(); }, [tab, loadAllPayments]);
  useEffect(() => { if (tab === 'reconciliation') loadRecons(); }, [tab, loadRecons]);
  useEffect(() => { if (tab === 'reports') loadReports(); }, [tab, loadReports]);

  const viewProofOfPayment = async (payment) => {
    if (!payment.pop_attachment) { toast.error('No proof of payment attached'); return; }
    const fileType = payment.pop_attachment.file_type || '';
    if (fileType.includes('pdf')) {
      try {
        setPopLoading(true);
        const response = await apiClient.get(`/payments/${payment.payment_id}/pop`, { responseType: 'blob' });
        window.open(URL.createObjectURL(response.data), '_blank');
      } catch (error) {
        toast.error('Failed to load PDF');
      } finally {
        setPopLoading(false);
      }
    } else {
      setViewingPOP({ filename: payment.pop_attachment.filename, fileType, paymentId: payment.payment_id });
      setPopImageUrl(null);
      setPopLoading(true);
      try {
        const response = await apiClient.get(`/payments/${payment.payment_id}/pop`, { responseType: 'blob' });
        setPopImageUrl(URL.createObjectURL(response.data));
      } catch (error) {
        toast.error('Failed to load proof of payment');
        setViewingPOP(null);
      } finally {
        setPopLoading(false);
      }
    }
  };

  const closePopModal = () => {
    if (popImageUrl) URL.revokeObjectURL(popImageUrl);
    setViewingPOP(null);
    setPopImageUrl(null);
  };

  const openVerifyDialog = (payment, action) => {
    setVerifyingPayment(payment);
    setVerifyAction(action);
    setVerifyNotes('');
  };

  const handleVerify = async () => {
    if (!verifyingPayment || !verifyAction) return;
    setSubmitting(true);
    try {
      await apiClient.post(
        `/admin/payments/${verifyingPayment.payment_id}/verify?status=${verifyAction}${verifyNotes ? `&notes=${encodeURIComponent(verifyNotes)}` : ''}`
      );
      toast.success(`Payment ${verifyAction === 'verified' ? 'verified' : 'rejected'} successfully`);
      setVerifyingPayment(null);
      setVerifyAction(null);
      setVerifyNotes('');
      loadPending();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  const getMemberName = (memberId) => {
    const member = members[memberId];
    return member ? `${member.first_name} ${member.last_name}` : memberId;
  };
  const getMemberEmail = (memberId) => members[memberId]?.email || 'N/A';

  const openReconcileDialog = (payment) => {
    setReconcilingPayment(payment);
    setReconForm({ reconciliation_type: 'duplicate_payment', reason: '', notes: '', adjustment_amount: '' });
  };

  const submitReconciliation = async () => {
    if (!reconcilingPayment) return;
    if (!reconForm.reason.trim()) { toast.error('A reason is required'); return; }
    setSubmittingRecon(true);
    try {
      const payload = {
        payment_id: reconcilingPayment.payment_id,
        reconciliation_type: reconForm.reconciliation_type,
        reason: reconForm.reason.trim(),
        notes: reconForm.notes.trim() || null,
      };
      if (reconForm.adjustment_amount !== '' && !isNaN(parseFloat(reconForm.adjustment_amount))) {
        payload.adjustment_amount = parseFloat(reconForm.adjustment_amount);
      }
      const res = await apiClient.post('/finance/reconciliations', payload);
      const noticeNo = res.data?.notice?.reconciliation_number;
      toast.success(noticeNo ? `Payment reconciled. Notice ${noticeNo} issued to the member.` : 'Payment reconciled successfully');
      setReconcilingPayment(null);
      loadAllPayments();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reconcile payment');
    } finally {
      setSubmittingRecon(false);
    }
  };

  const exportCsv = async () => {
    if (exportFrom && exportTo && exportFrom > exportTo) {
      toast.error('"From" date cannot be after "To" date');
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFrom) params.append('date_from', exportFrom);
      if (exportTo) params.append('date_to', exportTo);
      if (exportStatus && exportStatus !== 'all') params.append('status', exportStatus);
      const qs = params.toString();
      const res = await apiClient.get(`/reports/payments/csv${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = exportFrom || exportTo ? `_${exportFrom || 'start'}_to_${exportTo || 'end'}` : `_${new Date().toISOString().slice(0, 10)}`;
      link.setAttribute('download', `payments${suffix}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payments exported');
    } catch (error) {
      toast.error('Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  const exportAllPaid = async () => {
    setExportingAllPaid(true);
    try {
      const res = await apiClient.get('/reports/payments/all-paid/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_paid_members_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fully-paid members exported');
    } catch (error) {
      toast.error('Failed to export fully-paid members');
    } finally {
      setExportingAllPaid(false);
    }
  };


  // Derived: pending sorted by most recent submission
  const sortedPending = [...payments].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  const recentPending = sortedPending.slice(0, 5);
  const pendingTotalPages = Math.max(1, Math.ceil(sortedPending.length / PENDING_PAGE_SIZE));
  const safePendingPage = Math.min(pendingPage, pendingTotalPages);
  const pagedPending = sortedPending.slice(
    (safePendingPage - 1) * PENDING_PAGE_SIZE,
    safePendingPage * PENDING_PAGE_SIZE
  );

  return (
    <AdminLayout currentPage="payments" title="Payments">
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto" data-testid="payments-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="w-4 h-4 mr-1.5" />Dashboard</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            <Clock className="w-4 h-4 mr-1.5" />Pending
            {payments.length > 0 && <Badge className="ml-1.5 bg-amber-500 hover:bg-amber-500">{payments.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all"><ListChecks className="w-4 h-4 mr-1.5" />All Payments</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="tab-reconciliation"><Scale className="w-4 h-4 mr-1.5" />Reconciliation</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><FileText className="w-4 h-4 mr-1.5" />Reports</TabsTrigger>
        </TabsList>

        {/* ============ DASHBOARD ============ */}
        <TabsContent value="dashboard" className="space-y-4" data-testid="dashboard-content">
          {dashLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <Card data-testid="stat-today">
                  <CardContent className="pt-5 p-4">
                    <p className="text-xs text-muted-foreground">Collected Today</p>
                    <p className="text-xl md:text-2xl font-bold truncate">{formatCurrency(overview?.today_total || 0, currency)}</p>
                  </CardContent>
                </Card>
                <Card data-testid="stat-month">
                  <CardContent className="pt-5 p-4">
                    <p className="text-xs text-muted-foreground">Collected This Month</p>
                    <p className="text-xl md:text-2xl font-bold truncate">{formatCurrency(overview?.month_total || 0, currency)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200 cursor-pointer hover:border-amber-400 transition-colors" onClick={() => setTab('pending')} data-testid="stat-pending">
                  <CardContent className="pt-5 p-4">
                    <p className="text-xs text-amber-700">Pending Verification</p>
                    <p className="text-xl md:text-2xl font-bold text-amber-800">{overview?.pending_verification || 0}</p>
                    <p className="text-[11px] text-amber-600 mt-1">Click to review →</p>
                  </CardContent>
                </Card>
                <Card data-testid="stat-recon">
                  <CardContent className="pt-5 p-4">
                    <p className="text-xs text-muted-foreground">Reconciliations</p>
                    <p className="text-xl md:text-2xl font-bold">{overview?.reconciliations || 0}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-2" data-testid="sparkline-card">
                  <CardHeader className="p-4 md:p-6 pb-0">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                      <TrendingUp className="w-5 h-5 text-primary" /> Collections (Last 30 Days)
                    </CardTitle>
                    <CardDescription>Verified payments trend</CardDescription>
                  </CardHeader>
                  <CardContent className="p-2 md:p-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={overview?.sparkline || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="collGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} interval={4} />
                        <Tooltip formatter={(v) => formatCurrency(v, currency)} labelFormatter={(l) => l} />
                        <Area type="monotone" dataKey="amount" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#collGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card data-testid="top-outstanding-card">
                  <CardHeader className="p-4 md:p-6 pb-2">
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                      <AlertCircle className="w-5 h-5 text-red-500" /> Top Outstanding
                    </CardTitle>
                    <CardDescription>Members with highest balances</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {topOutstanding.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-6">No outstanding balances 🎉</p>
                    ) : (
                      topOutstanding.map((m) => (
                        <div
                          key={m.member_id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/admin/members/${m.member_id}/payments`)}
                          data-testid={`top-outstanding-${m.member_id}`}
                        >
                          <MemberAvatar photoUrl={m.portrait_photo_url} fullName={m.member_full_name} size="sm" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{m.member_full_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{m.member_email}</p>
                          </div>
                          <p className="text-sm font-bold text-red-600 whitespace-nowrap">{formatCurrency(m.outstanding_balance, currency)}</p>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Recent Pending Verification */}
              <Card data-testid="recent-pending-card">
                <CardHeader className="p-4 md:p-6 pb-2 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                      <Clock className="w-5 h-5 text-amber-600" /> Recent Pending Verification
                    </CardTitle>
                    <CardDescription>Latest 5 payment submissions awaiting review</CardDescription>
                  </div>
                  {sortedPending.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => setTab('pending')} data-testid="recent-pending-view-all">
                      View all ({sortedPending.length}) <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-2 md:p-4 pt-0">
                  {recentPending.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                      <p className="text-sm font-medium text-green-700">All caught up — nothing pending</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {recentPending.map((p) => (
                        <div
                          key={p.payment_id}
                          className="flex items-center gap-3 py-3 px-2 hover:bg-muted/50 rounded-lg transition-colors cursor-pointer"
                          onClick={() => setTab('pending')}
                          data-testid={`recent-pending-${p.payment_id}`}
                        >
                          <MemberAvatar
                            photoUrl={members[p.member_id]?.portrait_photo_url}
                            fullName={getMemberName(p.member_id)}
                            size="sm"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{getMemberName(p.member_id)}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {p.payment_method?.replace('_', ' ').toUpperCase()}
                              {p.payment_reference ? ` · Ref ${p.payment_reference}` : ''}
                            </p>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <p className="text-sm font-bold">{formatCurrency(p.amount, currency)}</p>
                            <p className="text-[11px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                          </div>
                          <Badge variant="outline" className={`${STATUS_STYLES.pending} hidden sm:inline-flex`}>
                            <Clock className="w-3 h-3 mr-1" /> Pending
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ============ PENDING ============ */}
        <TabsContent value="pending" data-testid="pending-content">
          <Card>
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                <AlertCircle className="w-5 h-5 text-amber-600" /> Payments Pending Verification
              </CardTitle>
              <CardDescription>Review and verify member payment submissions</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                  <p className="text-xl font-semibold text-green-700">All Caught Up!</p>
                  <p className="text-muted-foreground">No payments pending verification</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pagedPending.map((payment) => (
                    <div
                      key={payment.payment_id}
                      className="border rounded-lg p-4 bg-card hover:shadow-md transition-shadow"
                      data-testid={`pending-payment-${payment.payment_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            <p className="text-xl font-bold">{formatCurrency(payment.amount, currency)}</p>
                            <Badge variant="outline" className={STATUS_STYLES.pending}>
                              <Clock className="w-3 h-3 mr-1" /> Pending
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Member</p>
                              <p className="font-medium">{getMemberName(payment.member_id)}</p>
                              <p className="text-xs text-muted-foreground">{getMemberEmail(payment.member_id)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Payment Method</p>
                              <p className="font-medium">{payment.payment_method?.replace('_', ' ').toUpperCase()}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Reference</p>
                              <p className="font-medium">{payment.payment_reference || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Submitted</p>
                              <p className="font-medium">{new Date(payment.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          {payment.invoice_id && (
                            <p className="text-xs text-blue-600">
                              <FileText className="w-3 h-3 inline mr-1" /> Linked to Invoice: {payment.invoice_id}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {payment.pop_attachment && (
                            <Button variant="outline" size="sm" onClick={() => viewProofOfPayment(payment)} data-testid={`view-pop-${payment.payment_id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View POP
                            </Button>
                          )}
                          <Button variant="default" size="sm" onClick={() => openVerifyDialog(payment, 'verified')} className="bg-green-600 hover:bg-green-700" data-testid={`verify-${payment.payment_id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Verify
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => openVerifyDialog(payment, 'rejected')} data-testid={`reject-${payment.payment_id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/members/${payment.member_id}/payments`)} title="View member's payment history">
                            <User className="w-4 h-4 mr-1" /> History
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {pendingTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <p className="text-sm text-muted-foreground">Page {safePendingPage} of {pendingTotalPages} · {sortedPending.length} pending</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={safePendingPage <= 1} onClick={() => setPendingPage(safePendingPage - 1)} data-testid="pending-prev"><ChevronLeft className="w-4 h-4" /> Prev</Button>
                        <Button variant="outline" size="sm" disabled={safePendingPage >= pendingTotalPages} onClick={() => setPendingPage(safePendingPage + 1)} data-testid="pending-next">Next <ChevronRight className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ ALL PAYMENTS ============ */}
        <TabsContent value="all" data-testid="all-content">
          <Card>
            <CardHeader className="p-4 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base md:text-lg">All Payments</CardTitle>
                  <CardDescription>Complete payment history</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative w-full sm:w-64">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-9" placeholder="Search member or reference..." value={allSearch}
                      onChange={(e) => { setAllPage(1); setAllSearch(e.target.value); }} data-testid="all-payments-search" />
                  </div>
                  <Select value={allStatus} onValueChange={(v) => { setAllPage(1); setAllStatus(v); }}>
                    <SelectTrigger className="w-36" data-testid="all-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {allLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
              ) : allPayments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No payments found.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {allPayments.map((p) => (
                    <div key={p.payment_id} className="flex items-center gap-3 border rounded-lg p-3 hover:bg-muted/40 transition-colors" data-testid={`all-payment-${p.payment_id}`}>
                      <MemberAvatar photoUrl={p.member_portrait_photo_url} fullName={p.member_full_name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{p.member_full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {new Date(p.created_at).toLocaleDateString()} · {p.payment_method?.replace('_', ' ')} {p.payment_reference ? `· ${p.payment_reference}` : ''}
                        </p>
                      </div>
                      <p className="font-bold whitespace-nowrap">{formatCurrency(p.amount, currency)}</p>
                      <Badge variant="outline" className={STATUS_STYLES[p.status] || ''}>{p.status}</Badge>
                      {p.status === 'verified' && (
                        <Button variant="ghost" size="sm" className="text-orange-600" onClick={() => openReconcileDialog(p)} title="Reconcile this payment" data-testid={`reconcile-${p.payment_id}`}>
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/members/${p.member_id}/payments`)} title="Member history">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {allTotal > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">Page {allPage} of {allTotalPages} · {allTotal} payments</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={allPage <= 1} onClick={() => setAllPage(allPage - 1)}><ChevronLeft className="w-4 h-4" /> Prev</Button>
                    <Button variant="outline" size="sm" disabled={allPage >= allTotalPages} onClick={() => setAllPage(allPage + 1)}>Next <ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ RECONCILIATION ============ */}
        <TabsContent value="reconciliation" data-testid="reconciliation-content">
          <Card>
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg"><Scale className="w-5 h-5 text-primary" /> Reconciliation Ledger</CardTitle>
              <CardDescription>Append-only financial adjustments (read-only audit trail)</CardDescription>
            </CardHeader>
            <CardContent>
              {reconLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
              ) : recons.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Scale className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No reconciliation entries yet. Adjustments are created from a member's payment history.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recons.map((r) => (
                    <div key={r.reconciliation_id} className="flex items-center gap-3 border rounded-lg p-3" data-testid={`recon-${r.reconciliation_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{r.reason || r.reconciliation_type?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {new Date(r.created_at).toLocaleDateString()} · {r.reconciliation_type?.replace(/_/g, ' ')} · by {r.created_by_email || 'admin'}
                          {noticeMap[r.reconciliation_id] && ` · ${noticeMap[r.reconciliation_id].reconciliation_number}`}
                        </p>
                      </div>
                      <p className={`font-bold whitespace-nowrap ${(r.adjustment_amount || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {(r.adjustment_amount || 0) >= 0 ? '+' : ''}{formatCurrency(r.adjustment_amount || 0, currency)}
                      </p>
                      {noticeMap[r.reconciliation_id] && (
                        <a href={getUploadUrl(`gridfs:${noticeMap[r.reconciliation_id].pdf_file_id}`)} target="_blank" rel="noopener noreferrer" data-testid={`recon-notice-${r.reconciliation_id}`}>
                          <Button size="sm" variant="outline" title="Download Reconciliation Notice">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ REPORTS ============ */}
        <TabsContent value="reports" data-testid="reports-content">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Financial Summary</CardTitle>
                <CardDescription>Overview of payments and invoices</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-muted-foreground">Total Revenue</p><p className="text-xl font-bold text-green-700">{formatCurrency(summary?.total_revenue || 0, currency)}</p></div>
                <div><p className="text-xs text-muted-foreground">Pending Verification</p><p className="text-xl font-bold">{summary?.pending_verification || 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Paid Invoices</p><p className="text-xl font-bold">{summary?.paid_invoices_count || 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Unpaid Invoices</p><p className="text-xl font-bold text-amber-700">{summary?.unpaid_invoices_count || 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Overdue Invoices</p><p className="text-xl font-bold text-red-700">{summary?.overdue_invoices_count || 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Outstanding Amount</p><p className="text-xl font-bold text-red-700">{formatCurrency(summary?.total_unpaid_invoices_amount || 0, currency)}</p></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Export</CardTitle>
                <CardDescription>Download payment records for a period</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={exportFrom} max={exportTo || undefined} onChange={(e) => setExportFrom(e.target.value)} data-testid="export-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={exportTo} min={exportFrom || undefined} onChange={(e) => setExportTo(e.target.value)} data-testid="export-date-to" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={exportStatus} onValueChange={setExportStatus}>
                    <SelectTrigger data-testid="export-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(exportFrom || exportTo || exportStatus !== 'all') && (
                  <Button variant="ghost" size="sm" className="text-muted-foreground h-auto p-0" onClick={() => { setExportFrom(''); setExportTo(''); setExportStatus('all'); }} data-testid="export-clear-filters">
                    Clear filters
                  </Button>
                )}
                <Button className="w-full" onClick={exportCsv} disabled={exporting} data-testid="export-payments-csv">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {exporting ? 'Exporting...' : 'Export Payments (CSV)'}
                </Button>
                <p className="text-xs text-muted-foreground">Leave dates blank to export all records. Includes member names, amounts, methods and status.</p>

                <div className="border-t pt-3 mt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">Fully Paid Members</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Members who have paid 100% of their annual fee.</p>
                  <Button variant="outline" className="w-full" onClick={exportAllPaid} disabled={exportingAllPaid} data-testid="export-all-paid-csv">
                    {exportingAllPaid ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {exportingAllPaid ? 'Exporting...' : 'Export All Paid (CSV)'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* POP Viewer Modal */}
      <Dialog open={!!viewingPOP} onOpenChange={closePopModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Proof of Payment</DialogTitle></DialogHeader>
          {viewingPOP && (
            <div className="flex flex-col items-center">
              {popLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Loading...</span>
                </div>
              ) : viewingPOP.fileType?.includes('image') && popImageUrl ? (
                <img src={popImageUrl} alt="Proof of Payment" className="max-h-[60vh] object-contain rounded" />
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Document</p>
                </div>
              )}
              <p className="text-sm text-muted-foreground mt-2">{viewingPOP.filename}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog open={!!verifyingPayment} onOpenChange={() => setVerifyingPayment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{verifyAction === 'verified' ? 'Verify Payment' : 'Reject Payment'}</DialogTitle>
          </DialogHeader>
          {verifyingPayment && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="text-xl font-bold">{formatCurrency(verifyingPayment.amount, currency)}</p>
                <p className="text-sm text-muted-foreground mt-2">Member</p>
                <p className="font-medium">{getMemberName(verifyingPayment.member_id)}</p>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder={verifyAction === 'verified' ? 'Add verification notes...' : 'Reason for rejection...'}
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                />
              </div>
              {verifyAction === 'verified' && (
                <p className="text-sm text-green-600"><CheckCircle className="w-4 h-4 inline mr-1" /> This will mark the payment as verified and update the member's balance.</p>
              )}
              {verifyAction === 'rejected' && (
                <p className="text-sm text-red-600"><XCircle className="w-4 h-4 inline mr-1" /> This will reject the payment. The member will need to submit a new proof of payment.</p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setVerifyingPayment(null)}>Cancel</Button>
                <Button
                  onClick={handleVerify}
                  disabled={submitting}
                  className={verifyAction === 'verified' ? 'bg-green-600 hover:bg-green-700' : ''}
                  variant={verifyAction === 'rejected' ? 'destructive' : 'default'}
                  data-testid="confirm-verify-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : verifyAction === 'verified' ? <CheckCircle className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                  {submitting ? 'Processing...' : verifyAction === 'verified' ? 'Verify Payment' : 'Reject Payment'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Reconcile Dialog */}
      <Dialog open={!!reconcilingPayment} onOpenChange={(open) => !open && setReconcilingPayment(null)}>
        <DialogContent data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" /> Reconcile Payment
            </DialogTitle>
          </DialogHeader>
          {reconcilingPayment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Original payment</span>
                  <span className="font-bold">{formatCurrency(reconcilingPayment.amount, currency)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{reconcilingPayment.member_full_name} · {reconcilingPayment.payment_method} · {new Date(reconcilingPayment.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>The original payment record stays intact. A reconciliation adjustment is added to correct the member&apos;s balance and is fully audited.</span>
              </div>
              <div>
                <Label>Reconciliation Type</Label>
                <Select value={reconForm.reconciliation_type} onValueChange={(v) => setReconForm({ ...reconForm, reconciliation_type: v })}>
                  <SelectTrigger data-testid="recon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(RECON_TYPE_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason <span className="text-red-500">*</span></Label>
                <Textarea placeholder="Explain why this payment is being reconciled..." value={reconForm.reason}
                  onChange={(e) => setReconForm({ ...reconForm, reason: e.target.value })} data-testid="recon-reason-input" />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea placeholder="Additional notes..." value={reconForm.notes}
                  onChange={(e) => setReconForm({ ...reconForm, notes: e.target.value })} data-testid="recon-notes-input" />
              </div>
              <div>
                <Label>Adjustment Amount (Optional)</Label>
                <Input type="number" placeholder={`Leave blank for full reversal (−${reconcilingPayment.amount})`} value={reconForm.adjustment_amount}
                  onChange={(e) => setReconForm({ ...reconForm, adjustment_amount: e.target.value })} data-testid="recon-amount-input" />
                <p className="text-xs text-muted-foreground mt-1">Use a negative number to reduce the paid amount (e.g. −{reconcilingPayment.amount} to reverse). Leave blank to fully reverse this payment.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReconcilingPayment(null)} data-testid="recon-cancel-btn">Cancel</Button>
                <Button onClick={submitReconciliation} disabled={submittingRecon} className="bg-orange-600 hover:bg-orange-700" data-testid="recon-submit-btn">
                  {submittingRecon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  {submittingRecon ? 'Processing...' : 'Reconcile Payment'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPayments;
